import { groupBy, values } from "ramda";

import { createErrorHandler } from "@/app-utils/errors.ts";
import type { ISelectOption } from "@/app-utils/form.ts";
import { LOAD_PLAYBOOKS_FAILURE, LOAD_PLAYBOOKS_REQUEST, LOAD_PLAYBOOKS_SUCCESS } from "@/redux/action-types.ts";
import { filterPlaybooksByLicense } from "@/redux/modules/config.ts";
import type { ReduxAction } from "@/redux/redux-utils.ts";
import type { MapperWorkflowField } from "@/ui-modules/playbooks/editor/ExportedWorkflowFieldMapper.tsx";

import {
  type Attribute,
  type EventResource,
  type InputExperimentInput,
  type InputPlaybookConfig,
  NfParamSubTypeEnum,
  NfParamTypeEnum,
  type NfParamTypeInput,
  type Playbook,
  type PlaybookCategory,
  type PlaybookConfig,
  UiContextEnum,
} from "../graphql.js";
import {
  ModulesIcons,
  PlaybookCategoriesIcons,
  type PlaybookCategoryIcon,
  type SelectorIcon,
  SelectorIcons,
} from "../settings/assets-settings.js";
import {
  ATTRIBUTE_FILE_TYPES,
  EVENT_RESOURCE_TYPES,
  PLAYBOOK_CLASS,
  PLAYBOOK_TYPE,
  WORKFLOW_PARAM_USAGE,
} from "../settings/settings.js";
import { type IAttributeWithDefault } from "../ui-modules/admin/AddSampleTypeDialog.js";
import { type NfParamConfiguration } from "../ui-modules/playbooks/editor/NfParamsConfigurator.js";
import { isFileAttribute } from "./sample.js";
import { type Dictionary } from "./type.js";

export interface IPlaybookPage {
  pageIdentifier?: string;
  title?: string;
  label?: string;
  description?: string;
}

export const getIconElementForPlaybook = (p: Playbook): SelectorIcon => {
  const config: PlaybookConfig = p.config;
  const customPlaybook: ICustomPlaybook<PlaybookConfig> = { ...p, config };
  return getIconElementForCustomPlaybook(customPlaybook);
};
export const getIconElementForCustomPlaybook = (p: IConfiguredPlaybook): SelectorIcon => {
  const icon = p?.config?.icon ?? p?.nfConfig?.icon ?? p?.p2Config?.icon;
  switch (p?.type?.class) {
    // Restricted playbooks, provided by Selector as modules
    case PLAYBOOK_CLASS.BLAST: {
      return ModulesIcons.BLAST;
    }
    // User created playbooks
    case PLAYBOOK_CLASS.SAMPLE:
    case PLAYBOOK_CLASS.NGS: {
      // the customized icon ID is stored in the playbook config
      return values(SelectorIcons).find((i) => i.id === icon) ?? SelectorIcons.SAMPLE2;
    }
    default: {
      return p ? (values(SelectorIcons).find((i) => i.id === icon) ?? ModulesIcons.DEFAULT) : ModulesIcons.DEFAULT;
    }
  }
};
export const getIconForPlaybookCategory = (category: Partial<PlaybookCategory>): PlaybookCategoryIcon => {
  return (
    values(PlaybookCategoriesIcons).find((i: PlaybookCategoryIcon) => i.id === category?.imageName) ||
    PlaybookCategoriesIcons.DEFAULT
  );
};
export const fleshOutConfiguredAttributes = (
  configuredAttributesWithBasicInformation: Partial<IConfiguredAttribute>[],
  attributesWithAllData: Attribute[],
): IConfiguredAttribute[] => {
  if (!configuredAttributesWithBasicInformation) {
    return [];
  }
  // the selection  does contain only partial data (e.g. stored only the identifiers or so in JSON format) therefore we need to flesh out this selection with the available data
  return configuredAttributesWithBasicInformation // keep the order of the selection
    .map((selAttribute: Partial<IConfiguredAttribute>) => {
      // in the pre-selection we might have only a subset of the attributes information
      const attribute: Attribute = attributesWithAllData.find(
        (attribute: Attribute) => attribute.id === selAttribute.id,
      );
      if (attribute) {
        return {
          ...attribute, // with all attribute data
          ...selAttribute, // together with configuration
        };
      }
    })
    .filter((selAttribute: Attribute) => !!selAttribute); // in addition we might have changed the sampleType in the previous screen therefore the attributes might not be available, we need to filter them
};

export interface IConfiguredAttribute
  extends IAttributeWithDefault,
    Attribute,
    Pick<InputExperimentInput, "label" | "description" | "correctedTypeId" | "correctedMandatory"> {
  // attributeDefault is already included from IAttributeWithDefault
}

export type ISampleRegistrationPlaybookConfig = Pick<
  InputPlaybookConfig,
  "icon" | "experimentInput" | "sampleInput" | "sampleStatus"
>;

export interface ICombinedRegistrationNgsPlaybookConfig
  extends ISampleRegistrationPlaybookConfig,
    Pick<InputPlaybookConfig, "ngsWorkflow"> {}

export interface ICustomPlaybook<T> extends Omit<Playbook, "config"> {
  config: T;
}

export type ISampleRegistrationPlaybook = ICustomPlaybook<ISampleRegistrationPlaybookConfig>;
export type ICombinedRegistrationNgsPlaybook = ICustomPlaybook<ICombinedRegistrationNgsPlaybookConfig>;
export type IConfiguredPlaybook = ISampleRegistrationPlaybook | ICombinedRegistrationNgsPlaybook;

// type guard
export function isNgsPlaybook(playbook: IConfiguredPlaybook): playbook is ICombinedRegistrationNgsPlaybook {
  return !!(playbook as ICombinedRegistrationNgsPlaybook)?.config?.ngsWorkflow;
}

export function getPlaybookEventResourcesSummary(eventResources: EventResource[]): Dictionary<EventResource[]> {
  const resourceSummary: Dictionary<EventResource[]> = {};
  const resourceCategories = groupBy<EventResource>((res) => res.resourceType ?? "Unknown")(eventResources ?? []);
  Object.keys(resourceCategories).forEach((category: string) => {
    const resources = resourceCategories[category];
    let namedCategory = category;

    // try te retrieve type from extensions
    if (category === EVENT_RESOURCE_TYPES.UNKNOWN) {
      // const types = uniq(
      //   resources.map((res) => getFilenameInfo(res.file?.fileName)?.typeLabel).filter((typeLabel) => !!typeLabel),
      // ); // files might be hidden/protected
      // namedCategory = types.length && types.length <= 3 ? types.join(", ") : "Resource";

      // Do not infer the file type for now, see SEL9-1179
      namedCategory = "Resource";
    }

    resourceSummary[namedCategory] = resources;
  });
  return resourceSummary;
}

export const isRawDataFileTypeNfParam = (nfParam: NfParamConfiguration) => {
  // we have to check the type here because non-file nf params have "unknown" subtype as well, like "any file or folder" file attributes
  return nfParam?.type?.type === NfParamTypeEnum.File && isRawDataFileSubType(nfParam?.type?.subType);
};

export const isRawDataFileSubType = (subType: string) => {
  // "Unknown" is "any file or folder"
  switch (subType) {
    case NfParamSubTypeEnum.Aligned:
    case NfParamSubTypeEnum.Unaligned:
    case NfParamSubTypeEnum.UnalignedPaired:
    case NfParamSubTypeEnum.Unknown: {
      return true;
    }
    default: {
      return false;
    }
  }
};

export function isNgsDataFileAttribute(attribute: Attribute) {
  return (
    isFileAttribute(attribute) &&
    (attribute.fileType.id === ATTRIBUTE_FILE_TYPES.UNALIGNED_READ_DATA ||
      attribute.fileType.id === ATTRIBUTE_FILE_TYPES.ALIGNED_READ_DATA ||
      attribute.fileType.id === ATTRIBUTE_FILE_TYPES.ANY)
  );
}

export function getMatchingAttributeTypeFromParamUsage(workflowParamUsage: WORKFLOW_PARAM_USAGE): ATTRIBUTE_FILE_TYPES {
  if (workflowParamUsage === WORKFLOW_PARAM_USAGE.UNALIGNED_READS_INPUT_FILE_PATHS) {
    return ATTRIBUTE_FILE_TYPES.UNALIGNED_READ_DATA;
  }
  if (workflowParamUsage === WORKFLOW_PARAM_USAGE.ALIGNED_READS_INPUT_FILE_PATHS) {
    return ATTRIBUTE_FILE_TYPES.ALIGNED_READ_DATA;
  }
  return undefined;
}

export function getMatchingAttributeTypeFromNfParamType(paramConfigType: NfParamTypeInput): ATTRIBUTE_FILE_TYPES {
  if (
    (paramConfigType.type === NfParamTypeEnum.File && paramConfigType.subType === NfParamSubTypeEnum.Unaligned) ||
    (paramConfigType.type === NfParamTypeEnum.File && paramConfigType.subType === NfParamSubTypeEnum.UnalignedPaired)
  ) {
    return ATTRIBUTE_FILE_TYPES.UNALIGNED_READ_DATA;
  }
  if (paramConfigType.type === NfParamTypeEnum.File && paramConfigType.subType === NfParamSubTypeEnum.Aligned) {
    return ATTRIBUTE_FILE_TYPES.ALIGNED_READ_DATA;
  }
  return undefined;
}

export function loadPlaybooks(): ReduxAction<Promise<Playbook[]>> {
  return (dispatch, getState, webApiClient) => {
    dispatch({ type: LOAD_PLAYBOOKS_REQUEST });

    return webApiClient
      .fetchPlaybooks()
      .then((playbooks) => {
        const licensedPlaybooks: Playbook[] = filterPlaybooksByLicense(
          playbooks ?? [],
          getState().config.configuration,
        );

        dispatch({
          type: LOAD_PLAYBOOKS_SUCCESS,
          payload: licensedPlaybooks,
        });

        return licensedPlaybooks;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_PLAYBOOKS_FAILURE,
          alertTitle: "Fetching of List of Playbooks Failed",
        }),
      ) as Promise<Playbook[]>;
  };
}

export function isPlaybookExecutableInPlaybookPageContext(playbook: Playbook) {
  return (
    (!isPlaybookGenomeViewerType(playbook) || playbook.type.class === PLAYBOOK_CLASS.RANGE) &&
    // it must not be a P2 playbook, or a P2 playbook with the "playbook page" context
    (playbook.type.id !== PLAYBOOK_TYPE.PROCESSOR ||
      (playbook.type.id === PLAYBOOK_TYPE.PROCESSOR &&
        playbook.uiContexts?.find((c) => c === UiContextEnum.PlaybookPage)))
  );
}

export function isPlaybookExecutableInGenomeViewerContext(playbook: Playbook) {
  return (
    isPlaybookGenomeViewerType(playbook) ||
    // if it is a P2 playbook, it must have the "genome viewer" context
    (playbook.type.id === PLAYBOOK_TYPE.PROCESSOR && playbook.uiContexts?.find((c) => c === UiContextEnum.GenomeViewer))
  );
}

export function isPlaybookOnlyExecutableInGenomeViewerContext(playbook: Playbook) {
  return (
    (isPlaybookGenomeViewerType(playbook) && !(playbook.type.class === PLAYBOOK_CLASS.RANGE)) ||
    // if it is a P2 playbook, it must have the "genome viewer" context, but not the "playbook page" context
    (playbook.type.id === PLAYBOOK_TYPE.PROCESSOR &&
      !playbook.uiContexts?.find((c) => c === UiContextEnum.PlaybookPage) &&
      playbook.uiContexts?.find((c) => c === UiContextEnum.GenomeViewer))
  );
}

function isPlaybookGenomeViewerType(playbook: Playbook) {
  return (
    playbook.type.class === PLAYBOOK_CLASS.REPORT ||
    playbook.type.class === PLAYBOOK_CLASS.ALIGNMENT ||
    playbook.type.class === PLAYBOOK_CLASS.RANGE
  );
}

export function isKillablePlaybookType(playbook: Playbook | undefined) {
  if (!playbook) {
    return false;
  }
  return (
    playbook.type.id === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS ||
    playbook.type.id === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES ||
    playbook.type.id === PLAYBOOK_TYPE.REPORT_FROM_VARIANTS ||
    playbook.type.id === PLAYBOOK_TYPE.REPORT_FROM_COORDINATES ||
    playbook.type.id === PLAYBOOK_TYPE.ALIGNMENT ||
    playbook.type.id === PLAYBOOK_TYPE.PROCESSOR
  );
}

export function attributeFileTypeMatchesNfParamSubType(fileTypeId: number, paramSubType: NfParamSubTypeEnum): boolean {
  if (
    // the following matrix was agreed on with Devon
    paramSubType === NfParamSubTypeEnum.Unknown || // any file or folder params can take files from any/unaligned/aligned
    paramSubType === NfParamSubTypeEnum.File || // any file can take FILES from any/unaligned/aligned
    (paramSubType === NfParamSubTypeEnum.Folder && fileTypeId === ATTRIBUTE_FILE_TYPES.ANY) || // any file can take FOLDERS from any
    (paramSubType === NfParamSubTypeEnum.Unaligned && fileTypeId === ATTRIBUTE_FILE_TYPES.UNALIGNED_READ_DATA) || // obviously
    (paramSubType === NfParamSubTypeEnum.Aligned && fileTypeId === ATTRIBUTE_FILE_TYPES.ALIGNED_READ_DATA) // obviously
    // note that "paired unaligned read files" param subtype is currently not used and not required to work here
  ) {
    return true;
  }
  return false;
}

export function extractAttributeIdsFromMappingSelection(selection: any, field: string): number[] {
  const ids: number[] = [];
  selection?.forEach((s) => {
    if (s[field]) {
      ids.push(s[field].id);
    }
  });
  return ids;
}

export function NfParamConfigToMapperWorkflowField(nfParam: NfParamConfiguration): MapperWorkflowField {
  return {
    name: nfParam.id,
    label: nfParam.label,
    description: nfParam.description,
    isMultiValues: false,
    typeId: null,
    subType: nfParam.type.subType,
  };
}

export function getUiContextOptions(): ISelectOption[] {
  return [
    {
      value: UiContextEnum.GenomeViewer,
      label: "Genome Viewer",
    },
    { value: UiContextEnum.PlaybookPage, label: "Playbook Page" },
  ];
}
